import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, getDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { Container, Card, Table, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function CustomerFollow() {
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState({});
  const [follows, setFollows] = useState([]);
  const [staffNames, setStaffNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const currentUser = auth.currentUser;

  useEffect(() => {
    document.title = '顧客フォロー | My Order System';
    if (!currentUser) return;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // --- 1. 会社情報とフォロー情報を並列で取得 ---
        const [companiesSnapshot, followsSnapshot] = await Promise.all([
          getDocs(collection(db, 'companies')),
          getDocs(collection(db, 'follows'))
        ]);

        const companiesMap = {};
        companiesSnapshot.forEach(doc => { companiesMap[doc.id] = doc.data(); });
        setCompanies(companiesMap);

        const followsList = followsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFollows(followsList);

        // --- 2. ユーザー情報の取得（マッピング作成） ---
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const customersList = [];
        const namesMap = {};
        
        usersSnapshot.forEach(uDoc => {
          const data = uDoc.data();
          const uid = uDoc.id;
          namesMap[uid] = data.personName || data.email || '氏名未設定';
          
          if (data.userAttribute === '一般ユーザー') {
            customersList.push({ id: uid, ...data });
          }
        });

        // --- 3. 名前が取得できていない担当者を個別補完 ---
        const uniqueEmployeeIds = [...new Set(followsList.map(f => f.employeeId))];
        const missingIds = uniqueEmployeeIds.filter(id => !namesMap[id]);

        if (missingIds.length > 0) {
          await Promise.all(missingIds.map(async (id) => {
            try {
              const userDocSnap = await getDoc(doc(db, 'users', id));
              if (userDocSnap.exists()) {
                const d = userDocSnap.data();
                namesMap[id] = d.personName || d.email || '氏名未設定';
              }
            } catch (e) {
              console.warn(`担当者ID: ${id} の名前を個別取得できませんでした:`, e);
            }
          }));
        }

        setStaffNames(namesMap);
        setCustomers(customersList);

      } catch (err) {
        setError('データの取得に失敗しました。');
        console.error("Fetch Error:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [currentUser]);

  const handleFollowToggle = async (customerId, followDocId) => {
    try {
      if (followDocId) {
        await deleteDoc(doc(db, 'follows', followDocId));
      } else {
        await addDoc(collection(db, 'follows'), {
          employeeId: currentUser.uid,
          customerId: customerId,
          followedAt: serverTimestamp(),
        });
      }
      const followsSnapshot = await getDocs(collection(db, 'follows'));
      setFollows(followsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      alert('処理に失敗しました。');
      console.error(err);
    }
  };

  const filteredCustomers = useMemo(() => {
    // 【修正箇所】検索語が空の場合は空配列を返す（初期状態で何も表示しない）
    if (!searchTerm.trim()) return [];
    
    const lowercasedFilter = searchTerm.toLowerCase();
    
    return customers.filter(customer => {
      const company = companies[customer.companyId] || {};
      const companyName = company.companyName || '';
      const branchName = company.branchName || '';

      return (
        (customer.personName?.toLowerCase() || '').includes(lowercasedFilter) ||
        (customer.email?.toLowerCase() || '').includes(lowercasedFilter) ||
        (companyName.toLowerCase() || '').includes(lowercasedFilter) ||
        (branchName.toLowerCase() || '').includes(lowercasedFilter)
      );
    });
  }, [customers, searchTerm, companies]);

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex flex-column flex-md-row justify-content-md-between align-items-md-end">
            <h2 className="mb-2 mb-md-0">顧客フォロー</h2>
            <Link to="/mainmenu"><small>メインメニューに戻る</small></Link>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="氏名、会社名、支店名、メールアドレスで検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>

            {loading ? (
              <div className="text-center"><Spinner animation="border" /></div>
            ) : !searchTerm.trim() ? (
              // 【追加】検索キーワード未入力時の表示
              <div className="text-center text-muted my-5">
                <p>氏名、会社名、支店名などで顧客を検索してください。</p>
              </div>
            ) : filteredCustomers.length === 0 ? (
              // 【追加】検索結果が0件の場合の表示
              <div className="text-center text-muted my-5">
                <p>一致する顧客が見つかりませんでした。</p>
              </div>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>氏名</th>
                    <th>会社名</th>
                    <th>支店名</th>
                    <th>メールアドレス</th>
                    <th>担当状況</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => {
                    const company = companies[customer.companyId] || {};
                    const customerFollows = follows.filter(f => f.customerId === customer.id);
                    const myFollow = customerFollows.find(f => f.employeeId === currentUser.uid);
                    const othersFollow = customerFollows.filter(f => f.employeeId !== currentUser.uid);
                    
                    return (
                      <tr key={customer.id}>
                        <td>{customer.personName}</td>
                        <td>{company.companyPrefix}{company.companyName}{company.companySuffix}</td>
                        <td>{company.branchName || ''}</td>
                        <td>{customer.email}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {myFollow && (
                              <span className="badge bg-success text-start">
                                フォロー中: {'あなた'}
                              </span>
                            )}
                            {othersFollow.map(f => (
                              <span key={f.id} className="badge bg-secondary text-start">
                                フォロー中: {staffNames[f.employeeId] || staffNames[currentUser.uid]}
                              </span>
                            ))}
                            {customerFollows.length === 0 && (
                              <span className="badge bg-light text-dark border">未担当</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <Button
                            variant={myFollow ? "outline-danger" : "primary"}
                            size="sm"
                            onClick={() => handleFollowToggle(customer.id, myFollow?.id)}
                          >
                            {myFollow ? "フォロー解除" : "フォローする"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            <hr className="my-4" />
            <Link to="/mainmenu">メインメニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default CustomerFollow;